import './product-finder.min.css';
import './App.css';
import React, { useEffect, useState } from 'react';
import Loader from './Loader';
import Filters from './Filters';
import SortFilter from './SortFilter';
import Results from './Results';
import Count from './Count';
import { scrollerBack, sortBy } from './Util';

const App = () => {
  const baseUrl = 'https://www.burrisoptics.com';
  // const baseUrl = 'http://burrisoptics.test';
  const endpoint = '/api/v1/product-finder-api?XDEBUG_SESSION_START=PHPSTORM';
  const [loaded, setLoaded] = useState(false);
  const [count, setCount] = useState('');
  const [originData, setOriginData] = useState('');
  const [originFilters, setOriginFilters] = useState('');
  const [currentData, setCurrentData] = useState('');
  const [currentFilters, setCurrentFilters] = useState('');
  const [query, setQuery] = useState([]);
  const [sortOrder, setSortOrder] = useState('high');

  /**
   * Reset result status to all true.
   */
  const reset = async (clear = false) => {
    const currentDataCopy = [...currentData];

    if (clear) {
      Object.keys(currentDataCopy).forEach((el) => {
        currentDataCopy[el].active = true;
      });

      setQuery([]);
      setCount(currentDataCopy.length);
      setCurrentData(currentDataCopy);
      scrollerBack();
    }
  };

  /**
   * Update the current query.
   *
   * If an inactive button is clicked:
   *  - Reset result set to all active.
   *  - Clear query and add inactive value to query.
   *
   * @param {*} active
   *   Status of clicked filter button.
   * @param {*} queryValue
   *   Value from clicked filter button.
   *
   * @returns
   *   An updated query object to filter results.
   */
  const updateQuery = async (active, queryValue) => {
    let queryCopy = [...query];

    // If type is number, update that query value, don't add to it or remove it.
    if (queryValue.type === 'number') {
      const filterKey = Object.keys(queryCopy).find(
        (el) => queryCopy[el].field === queryValue.field
      );

      if (filterKey) {
        queryCopy[filterKey].tid = queryValue.tid;
      } else {
        queryCopy.push(queryValue);
      }
    } else {
      const included = Object.keys(queryCopy).some(
        (el) =>
          queryCopy[el].field === queryValue.field &&
          queryCopy[el].tid === queryValue.tid
      );

      // If clicking an inactive option.
      // If query already contains the value, remove it.
      // Otherwise, add the query.
      if (active === 'false') {
        reset('clear');
        queryCopy = [];
        queryCopy.push(queryValue);
      } else if (included) {
        queryCopy = Object.keys(queryCopy).filter((key) => key === included);
      } else {
        queryCopy.push(queryValue);
      }
    }

    return queryCopy;
  };

  const handleFilter = async (target) => {
    const field = target.getAttribute('data-field');
    const tid = target.getAttribute('data-tid');
    const type = target.getAttribute('data-type');
    const active = target.getAttribute('data-status');
    const { checked } = target;
    const currentDataCopy = [...originData];
    const currentFiltersCopy = currentFilters;
    const queryValue = {
      field,
      tid,
      type,
    };

    // 1. Update the query object based on current selection.
    const updatedQuery = await updateQuery(active, queryValue);
    setQuery(updatedQuery);

    filterResults(updatedQuery, currentDataCopy, currentFiltersCopy, true);
  };

  const handleSlider = async (data, sliderVal) => {
    const currentDataCopy = [...originData];
    const currentFiltersCopy = currentFilters;
    const queryValue = {
      field: data.field,
      tid: sliderVal,
      type: 'number',
    };
    const updatedQuery = await updateQuery(true, queryValue);
    setQuery(updatedQuery);

    filterResults(updatedQuery, currentDataCopy, currentFiltersCopy, false);
  };

  const filterResults = (
    updatedQuery,
    currentDataCopy,
    currentFiltersCopy,
    scroller
  ) => {
    const activeCount = [];

    // 2. If there's a query object with values, set all data and filters to false. Else set all data and filters to true.
    if (Object.keys(updatedQuery).length > 0) {
      // 2a. Set all data results to false before looping with new query object.
      Object.keys(currentDataCopy).forEach((el) => {
        const thisNode = currentDataCopy[el];
        thisNode.active = false;
      });

      // 2b. Set all filters to false before looping with new query object so they can be set naturally.
      Object.keys(currentFiltersCopy).forEach((el) => {
        const thisFilterValues = currentFiltersCopy[el].value;

        Object.keys(thisFilterValues).forEach((i) => {
          if (currentFiltersCopy[el].type !== 'number') {
            currentFiltersCopy[el].value[i].active = false;
          }
        });
      });
    } else {
      // 2a. Set all data results to true.
      Object.keys(currentDataCopy).forEach((el) => {
        const thisNode = currentDataCopy[el];
        thisNode.active = true;
      });

      // 2b. Set all filters to true.
      Object.keys(currentFiltersCopy).forEach((el) => {
        const thisFilterValues = currentFiltersCopy[el].value;

        Object.keys(thisFilterValues).forEach((i) => {
          if (currentFiltersCopy[el].type !== 'number') {
            currentFiltersCopy[el].value[i].active = true;
            // currentFiltersCopy[el].value[i].checked = false;
          }
        });
      });
    }

    // 3. Loop through query/queries to set data true/false.
    updatedQuery.forEach((q, k) => {
      const arrField = q.field;
      const arrType = q.type;
      let arrValue = '';

      // Loop through nodes on each query.
      Object.keys(currentDataCopy).forEach((el) => {
        const thisNode = currentDataCopy[el];
        const thisNodesValues = thisNode.value[arrField];

        // If this is the first query AND the value is present, set active.
        // Else If not the first query AND already active, keep active.
        // Else inactive.
        if (arrType === 'number') {
          const extractedValues = thisNodesValues.map((x) => parseInt(x));
          const thisValMin = Math.min(...extractedValues);
          const thisValMax = Math.max(...extractedValues);

          arrValue = q.tid;
          if (
            k === 0 &&
            thisValMin >= arrValue.min &&
            thisValMax <= arrValue.max
          ) {
            thisNode.active = true;
          } else if (
            thisNode.active &&
            thisValMin >= arrValue.min &&
            thisValMax <= arrValue.max
          ) {
            thisNode.active = true;
          } else {
            thisNode.active = false;
          }
        } else {
          arrValue = parseInt(q.tid);
          if (k === 0 && thisNodesValues.includes(arrValue)) {
            thisNode.active = true;
          } else if (
            thisNode.active === true &&
            thisNodesValues.includes(arrValue)
          ) {
            thisNode.active = true;
          } else {
            thisNode.active = false;
          }
        }
      });
    });

    // 4. Loop through updated data counting active results and creating new filter object.
    Object.keys(currentDataCopy).forEach((el) => {
      const thisNode = currentDataCopy[el];

      if (thisNode.active) {
        activeCount.push(thisNode.nid);
      }
    });

    // Set data state.
    setCount(activeCount.length);
    const sortedData = sortBy(currentDataCopy, sortOrder);
    setCurrentData(sortedData);

    if (scroller) {
      scrollerBack();
    }
  };

  const handleSort = (e) => {
    const currentDataCopy = [...currentData];

    setSortOrder(e.currentTarget.value);
    const sortedData = sortBy(currentDataCopy, e.currentTarget.value);
    setCurrentData(sortedData);
  };

  const fetchData = async (token) => {
    const myHeaders = new Headers();
    let raw = {};
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append('X-CSRF-Token', token);

    if (
      window.drupalSettings !== undefined &&
      window.drupalSettings.term !== undefined
    ) {
      raw = {
        tid: window.drupalSettings.term.termId,
        vid: window.drupalSettings.term.vid,
      };
    } else {
      // Fixed term id for local server testing.
      // (Prevents needing to load Drupal page.)
      raw = {};
    }

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify(raw),
      redirect: 'follow',
    };

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, requestOptions);
      const json = await response.json();
      setOriginData(Object.values(json.data));
      setOriginFilters(json.filters);

      const sortedData = sortBy(Object.values(json.data), 'high');

      setCurrentData(sortedData);
      setCurrentFilters(json.filters);

      const currentCount = Object.values(json.data).length;
      setCount(currentCount);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await fetch(`${baseUrl}/session/token`);
        const json = await response.text();
        await fetchData(json);
        setLoaded(true);
      } catch (error) {
        console.log('error', error);
      }
    };

    fetchToken();
    reset();
    // Loop through data objects.
    // Create filter groups.
  }, []);

  return (
    <div className={`product-finder-app ${loaded ? 'loaded' : ''}`}>
      {!loaded && <Loader />}
      {loaded && (
        <>
          <div className="product-finder-app-container">
            <div className="filters-header">
              <h3>Shop By</h3>
            </div>
            <div className="results-header">
              <SortFilter sortOrder={sortOrder} handleSort={handleSort} />
              {Object.keys(currentData) && <Count count={count} />}
            </div>
          </div>
          <div className="product-finder-app-container">
            <Filters
              originData={originData}
              originFilters={originFilters}
              currentData={currentData}
              currentFilters={currentFilters}
              query={query}
              handleFilter={handleFilter}
              handleSlider={handleSlider}
              reset={reset}
              scrollerBack={scrollerBack}
              count={count}
            />
            <Results currentData={currentData} count={count} />
          </div>{' '}
        </>
      )}
    </div>
  );
};

export default App;
